import { z } from 'zod';
import {
    parseJsonWithNativeSafeNumbers,
    stringifyLosslessJson,
} from '../../lossless-json-codec';
import { CloverEcommerceTokenizationSuccessResponseSchema } from '../../schemas/ipg/clover/clover-tokenization.schemas';
import {
    CloverTokenizationResultType,
    type CloverEcommerceBrowserTokenizationResult,
} from '../../types/ipg/clover/clover-tokenization.types';

export const CloverBrowserCardBrandName = {
    VISA: 'Visa',
    MASTERCARD: 'Mastercard',
    AMERICAN_EXPRESS: 'AmEx',
    DISCOVER: 'Discover',
    DINERS_CLUB: 'Diners Club',
    JCB: 'JCB',
} as const;

export type CloverBrowserCardBrandName =
    (typeof CloverBrowserCardBrandName)[keyof typeof CloverBrowserCardBrandName];

export interface CloverBrowserCardData {
    readonly cardNumber: string;
    readonly expiryDate: string;
    readonly cvv: string;
    readonly cardholderName: string;
    readonly cardBrand: CloverBrowserCardBrandName;
}

export type CloverBrowserTokenizationFetch = (
    input: RequestInfo | URL,
    init?: RequestInit
) => Promise<Response>;

export interface CloverBrowserTokenizationInput {
    readonly cardData: CloverBrowserCardData;
    readonly ecommerceApiKey: string;
    readonly ecommerceTokenizationUrl: string;
    readonly fetchImplementation?: CloverBrowserTokenizationFetch;
}

export interface CloverEcommerceTokenizationRequest {
    readonly url: string;
    readonly init: RequestInit;
}

export interface CloverFrontendTokenizationAdapter {
    readonly tokenizeCard: (
        input: CloverBrowserTokenizationInput
    ) => Promise<CloverEcommerceBrowserTokenizationResult>;
}

export const CloverBrowserCardDataSchema: z.ZodType<CloverBrowserCardData> = z.object({
    cardNumber: z.string().min(1),
    expiryDate: z.string().regex(/^\d{2}\/\d{2}$/),
    cvv: z.string().regex(/^\d{3,4}$/),
    cardholderName: z.string().trim().min(1),
    cardBrand: z.enum([
        CloverBrowserCardBrandName.VISA,
        CloverBrowserCardBrandName.MASTERCARD,
        CloverBrowserCardBrandName.AMERICAN_EXPRESS,
        CloverBrowserCardBrandName.DISCOVER,
        CloverBrowserCardBrandName.DINERS_CLUB,
        CloverBrowserCardBrandName.JCB,
    ]),
});

export const CloverBrowserTokenizationRequestInputSchema: z.ZodType<
    Omit<CloverBrowserTokenizationInput, 'fetchImplementation'>
> = z.object({
    cardData: CloverBrowserCardDataSchema,
    ecommerceApiKey: z.string().trim().min(1),
    ecommerceTokenizationUrl: z.string().url(),
});

type CloverEcommerceCardBrand = 'VISA' | 'MC' | 'AMEX' | 'DISCOVER' | 'DINERS_CLUB' | 'JCB';

function resolveCloverEcommerceCardBrand(
    cardBrand: CloverBrowserCardBrandName
): CloverEcommerceCardBrand {
    switch (cardBrand) {
        case CloverBrowserCardBrandName.VISA:
            return 'VISA';
        case CloverBrowserCardBrandName.MASTERCARD:
            return 'MC';
        case CloverBrowserCardBrandName.AMERICAN_EXPRESS:
            return 'AMEX';
        case CloverBrowserCardBrandName.DISCOVER:
            return 'DISCOVER';
        case CloverBrowserCardBrandName.DINERS_CLUB:
            return 'DINERS_CLUB';
        case CloverBrowserCardBrandName.JCB:
            return 'JCB';
    }
}

export function buildCloverEcommerceTokenizationRequest(
    input: Omit<CloverBrowserTokenizationInput, 'fetchImplementation'>
): CloverEcommerceTokenizationRequest {
    const parsedInput: Omit<CloverBrowserTokenizationInput, 'fetchImplementation'> =
        CloverBrowserTokenizationRequestInputSchema.parse(input);
    const normalizedPan: string = parsedInput.cardData.cardNumber.replace(/\D/g, '');
    if (normalizedPan.length < 14 || normalizedPan.length > 19) {
        throw new Error('Clover card tokenization requires a valid card number.');
    }
    const expiryParts: ReadonlyArray<string> = parsedInput.cardData.expiryDate.split('/');
    const expMonth: string | undefined = expiryParts[0];
    const expYear: string | undefined = expiryParts[1];
    if (expMonth === undefined || expYear === undefined) {
        throw new Error('Clover card tokenization requires an expiration date.');
    }

    const parsedUrl: URL = new URL(parsedInput.ecommerceTokenizationUrl);
    parsedUrl.search = '';
    parsedUrl.hash = '';
    const normalizedUrl: string = parsedUrl.toString().replace(/\/+$/, '');

    return {
        url: normalizedUrl,
        init: {
            method: 'POST',
            headers: {
                accept: 'application/json',
                apikey: parsedInput.ecommerceApiKey,
                'content-type': 'application/json',
            },
            body: stringifyLosslessJson({
                card: {
                    number: normalizedPan,
                    first6: normalizedPan.slice(0, 6),
                    last4: normalizedPan.slice(-4),
                    exp_month: expMonth,
                    exp_year: `20${expYear}`,
                    cvv: parsedInput.cardData.cvv,
                    brand: resolveCloverEcommerceCardBrand(parsedInput.cardData.cardBrand),
                    name: parsedInput.cardData.cardholderName.trim(),
                },
            }),
        },
    };
}

export async function tokenizeCardWithCloverEcommerce(
    input: CloverBrowserTokenizationInput
): Promise<CloverEcommerceBrowserTokenizationResult> {
    const tokenizationRequest: CloverEcommerceTokenizationRequest =
        buildCloverEcommerceTokenizationRequest(input);
    const fetchImplementation: CloverBrowserTokenizationFetch = input.fetchImplementation ?? fetch;
    const response: Response = await fetchImplementation(
        tokenizationRequest.url,
        tokenizationRequest.init
    );
    const responseBody: unknown = await parseJsonResponse(response);

    if (!response.ok) {
        throw new Error(`Clover card tokenization failed with HTTP ${response.status}.`);
    }

    const successParseResult =
        CloverEcommerceTokenizationSuccessResponseSchema.safeParse(responseBody);
    if (!successParseResult.success) {
        throw new Error('Clover card tokenization response is missing expected fields.');
    }

    return {
        result_type: CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION,
        token_response: successParseResult.data,
    };
}

async function parseJsonResponse(response: Response): Promise<unknown> {
    try {
        const responseText: string = await response.text();
        return parseJsonWithNativeSafeNumbers(responseText);
    } catch {
        throw new Error('Clover card tokenization response was not valid JSON.');
    }
}

export const CloverEcommerceBrowserTokenizationAdapter: CloverFrontendTokenizationAdapter = {
    tokenizeCard: tokenizeCardWithCloverEcommerce,
};
